import { action, computed } from '@ember/object';
import { inject as service } from '@ember/service';
import { or, notEmpty } from '@ember/object/computed';
import Controller, { inject as controller } from '@ember/controller';
import { next } from '@ember/runloop';

import { defaultTheme } from '@customerio/pluma-components/css';
// import { plumaContextValueStore, plumaThemeStore, plumaIconManagerStore } from '@customerio/hydra-pluma-state';
import { plumaContextValueStore, plumaIconManagerStore } from '@customerio/hydra-pluma-state';
import { tracked } from '@glimmer/tracking';

export default class ApplicationController extends Controller {
  @service account;
  @service alerts;
  @service auth;
  // Even though this isn't referenced in this controller or its template,
  // the analytics server has some initialization logic for the vwb demo
  // and thus needs to stay in!
  @service analytics;
  @service apiErrors;
  @service masq;
  @service router;
  @service session;
  @service hydra;

  @controller app;

  @tracked plumaContextValue = plumaContextValueStore?.get();
  get plumaComponentFlags() {
    return this.plumaContextValue?.componentFlags ?? { unsafe_useLegacyButton: true };
  }
  @tracked plumaTheme = defaultTheme;
  @tracked plumaIconManager = plumaIconManagerStore.get();

  @notEmpty('account.current') isLoggedIn;

  @or('masq.isAllowed', 'masq.isOn') hasMasqueradeBar;

  @computed('auth.isReauthenticating', 'session.isAuthenticated')
  get showPasswordModal() {
    return this.auth.isReauthenticating && this.session.isAuthenticated;
  }

  // builds dot-separated current route path, starting after base "authenticated" route
  // unlike "router.currentRouteName", it doesn't return only path element after last route definition with "resetNamespace: true"
  @computed('router.currentRoute', 'router.currentRouteName')
  get fullAuthenticatedCurrentPath() {
    let { currentRoute, currentRouteName } = this.router;

    // https://github.com/emberjs/ember.js/issues/19449
    // currentRoute currently doesn't change for loading state. Until it's fixed
    // in a newer version, we'll return currentRouteName instead, if it's a
    // loading state.
    // This isn't ideal, because currentRouteName doesn't contain the full path
    // (as described in the comment above with resetNamespace), but I can't see
    // a quick and sane way to build these route names otherwise
    if ((currentRouteName ?? '').includes('loading')) {
      return currentRouteName;
    }

    if (!currentRoute) {
      return;
    }

    let allActiveRoutes = [currentRoute];
    while (allActiveRoutes[0].parent && allActiveRoutes[0].parent.name !== 'authenticated') {
      allActiveRoutes.unshift(allActiveRoutes[0].parent);
    }

    // ensure each route name contains single path level, to avoid duplicated parts
    let allActiveRoutesPathElements = allActiveRoutes.map((route) => route.name.split('.').pop());

    return allActiveRoutesPathElements.join('.');
  }

  @computed('fullAuthenticatedCurrentPath')
  get classForCurrentPath() {
    let { fullAuthenticatedCurrentPath } = this;

    if (!fullAuthenticatedCurrentPath) {
      return;
    }

    return `${fullAuthenticatedCurrentPath.replace(/\./g, '-')}-route`;
  }

  @action
  clearApiError() {
    this.apiErrors.clear();
  }

  @action
  removeAlert(id) {
    // Because of a bug with Ember's `LinkTo` component and how it handles the
    // {{on "click"}} modifier, removing the element while ember is still
    // working with it will cause the app to be reloaded. To avoid that, we will
    // remove the alert on the next tick, once Ember has emptied its function
    // stack for the `LinkTo` component.
    next(() => {
      this.alerts.remove(id);
    });
  }
}
